/**
 * Environment variables resolvable at runtime.
 *
 * @see public/env-config.js
 */
export type RuntimeEnvName =
    | "VITE_SUPPORT_URL"
    | "VITE_DOC_API_URL"
    | "VITE_LEGAL_TERMS_URL"
    | "VITE_USER_GUIDE_URL"
    | "VITE_CGU_URL"
    | "VITE_COOKIES_URL"

/**
 * Reads an environment variable: the build-time value first (`import.meta.env`, fed by
 * a local .env during development), then the runtime one (`window.configs`, written by
 * the container entrypoint into /env-config.js at every start). Production images are
 * built without these variables, so their values can be changed by restarting the
 * container — no rebuild.
 *
 * An empty value counts as unset: the entrypoint expands every missing container
 * variable to an empty string, which must not shadow the fallback.
 *
 * @param {RuntimeEnvName} name - The variable to read.
 * @param {string} [fallback] - The value used when the variable is set nowhere.
 * @returns {string} The resolved value.
 */
export const getEnv = (name: RuntimeEnvName, fallback?: string): string =>
    import.meta.env?.[name] || window?.configs?.[name] || fallback || ""
